import { useState } from "react";
import { ThumbsUp, ThumbsDown } from "@phosphor-icons/react";
import PeopleAutocomplete from "@/components/PeopleAutocomplete";

const processTypes = ["Replenishment","Targeted Outs","Pricing","Planograms","Truck","Backstocking","Special Event Prep"];
// Which task types collect a size measure and what unit label to show.
const TASK_SIZE_UNIT = {
  Replenishment: "pages",
  Truck: "pieces",
  Planograms: "planograms",
  Poggs: "planograms", // legacy value preserved for existing entries
};
const kindHeading = { conversation: "Conversation", plan: "Plan", process: "Task", interview: "Interview", opportunity: "Opportunity", wisdom: "Lesson Learned", win: "Win" };

export default function SubEntryForm({ kind, initial = null, onSave, onCancel }) {
  // Only treat as edit when we have a full existing sub-entry (with an id).
  const isEdit = !!(initial && initial.id);
  const [subject, setSubject] = useState(initial?.subject || "");
  const [participants, setParticipants] = useState(initial?.participants || []);
  const [notes, setNotes] = useState(initial?.notes || "");
  const initialProcessType = initial?.process_type && processTypes.includes(initial.process_type)
    ? initial.process_type
    : (initial?.process_type || processTypes[0]);
  const [processType, setProcessType] = useState(initialProcessType);
  const [leader, setLeader] = useState(initial?.leader || "");
  const [team, setTeam] = useState(initial?.team || []);
  // 1 = thumbs up (good), 0 = thumbs down (bad), null = not rated. Legacy 1..5 gets normalised on load.
  const normaliseOutcome = (v) => {
    if (v === null || v === undefined) return null;
    if (v === 1 || v === 2) return 1;
    if (v === 0 || v === 4 || v === 5) return 0;
    return null;
  };
  const [outcomeRating, setOutcomeRating] = useState(normaliseOutcome(initial?.outcome_rating));
  const initHours = initial?.duration_minutes != null ? Math.floor(initial.duration_minutes / 60) : "";
  const initMinutes = initial?.duration_minutes != null ? initial.duration_minutes % 60 : "";
  const [hours, setHours] = useState(initHours);
  const [minutes, setMinutes] = useState(initMinutes);
  const [sizeCount, setSizeCount] = useState(initial?.size_count ?? "");
  const [position, setPosition] = useState(initial?.position || "");
  const [applicantName, setApplicantName] = useState(initial?.applicant_name || "");
  const [rating, setRating] = useState(initial?.rating ?? 7);
  const [submitting, setSubmitting] = useState(false);

  const sizeUnit = TASK_SIZE_UNIT[processType];

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return; // guard against double-submit (Enter key + Save click)
    const payload = { kind, notes };
    if (kind === "conversation") { payload.subject = subject; payload.participants = participants.filter(Boolean); }
    if (kind === "plan") { payload.subject = subject; }
    if (kind === "win") { payload.subject = subject; payload.participants = participants.filter(Boolean); }
    if (kind === "process") {
      payload.process_type = processType;
      payload.leader = leader;
      payload.team = team.filter(Boolean);
      if (outcomeRating !== null) payload.outcome_rating = outcomeRating;
      const h = Number(hours) || 0;
      const m = Number(minutes) || 0;
      if (h > 0 || m > 0) payload.duration_minutes = h * 60 + m;
      if (sizeUnit && sizeCount !== "" && sizeCount !== null) payload.size_count = Number(sizeCount);
    }
    if (kind === "interview") { payload.position = position; payload.applicant_name = applicantName; payload.rating = Number(rating); }
    setSubmitting(true);
    try {
      // onSave may be sync or async — awaiting handles both
      await onSave(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const inp = "w-full rounded-xl border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400";
  const lbl = "text-xs font-semibold uppercase tracking-[0.14em] text-slate-500";

  return (
    <form onSubmit={submit} className="border border-slate-200/70 rounded-xl p-4 mb-3 bg-slate-50/40" data-testid={`form-${kind}${isEdit ? "-edit" : ""}`}>
      <div className="text-sm font-semibold mb-3 al-gradient-text uppercase tracking-widest">{isEdit ? `Edit ${kindHeading[kind] || kind}` : (kindHeading[kind] || kind)}</div>
      <div className="grid md:grid-cols-2 gap-3">
        {(kind === "conversation" || kind === "plan" || kind === "win") && (
          <div className="md:col-span-2">
            <label className={lbl}>{kind === "win" ? "What was the win?" : "Subject"}</label>
            <input data-testid="sub-subject" required value={subject} onChange={e=>setSubject(e.target.value)} className={inp} placeholder={kind === "win" ? "e.g. Closed the Q1 renewal with Acme Corp" : undefined}/>
          </div>
        )}
        {kind === "conversation" && (
          <div className="md:col-span-2">
            <label className={lbl}>Participants</label>
            <PeopleAutocomplete mode="multi" value={participants} onChange={setParticipants} testid="sub-participants" placeholder="Add a name and press Enter…" />
          </div>
        )}
        {kind === "win" && (
          <div className="md:col-span-2">
            <label className={lbl}>People involved</label>
            <PeopleAutocomplete mode="multi" value={participants} onChange={setParticipants} testid="sub-participants" placeholder="Who helped make this happen?" />
          </div>
        )}
        {kind === "process" && <>
          <div>
            <label className={lbl}>Task Type</label>
            <select data-testid="sub-process-type" value={processType} onChange={e=>setProcessType(e.target.value)} className={inp}>
              {processTypes.map(t => <option key={t}>{t}</option>)}
              {initial?.process_type && !processTypes.includes(initial.process_type) && (
                <option key={initial.process_type}>{initial.process_type}</option>
              )}
            </select>
          </div>
          <div>
            <label className={lbl}>Leader</label>
            <PeopleAutocomplete mode="single" value={leader} onChange={setLeader} testid="sub-leader" />
          </div>
          <div className="md:col-span-2">
            <label className={lbl}>Team</label>
            <PeopleAutocomplete mode="multi" value={team} onChange={setTeam} testid="sub-team" placeholder="Add team members…" />
          </div>
          <div className={sizeUnit ? "" : "md:col-span-2"}>
            <label className={lbl}>Duration</label>
            <div className="flex gap-2 mt-1">
              <div className="flex-1">
                <input data-testid="sub-duration-hours" type="number" min="0" placeholder="0" value={hours} onChange={e=>setHours(e.target.value)} className={inp}/>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1 text-center">hours</div>
              </div>
              <div className="flex-1">
                <input data-testid="sub-duration-minutes" type="number" min="0" max="59" placeholder="0" value={minutes} onChange={e=>setMinutes(e.target.value)} className={inp}/>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1 text-center">minutes</div>
              </div>
            </div>
          </div>
          {sizeUnit && (
            <div>
              <label className={lbl}>Size ({sizeUnit})</label>
              <input data-testid="sub-size" type="number" min="0" placeholder="0" value={sizeCount} onChange={e=>setSizeCount(e.target.value)} className={inp}/>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1 text-center">{sizeUnit}</div>
            </div>
          )}
          <div className="md:col-span-2">
            <label className={lbl}>Outcome</label>
            <div className="mt-2 flex gap-2">
              <button type="button" data-testid="sub-outcome-up" onClick={()=>setOutcomeRating(1)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${outcomeRating === 1 ? "bg-emerald-500 text-white border-emerald-500" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                <ThumbsUp weight={outcomeRating === 1 ? "fill" : "regular"} size={18}/> Good
              </button>
              <button type="button" data-testid="sub-outcome-down" onClick={()=>setOutcomeRating(0)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${outcomeRating === 0 ? "bg-rose-500 text-white border-rose-500" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                <ThumbsDown weight={outcomeRating === 0 ? "fill" : "regular"} size={18}/> Bad
              </button>
            </div>
          </div>
        </>}
        {kind === "interview" && <>
          <div>
            <label className={lbl}>Position</label>
            <input data-testid="sub-position" required value={position} onChange={e=>setPosition(e.target.value)} className={inp}/>
          </div>
          <div>
            <label className={lbl}>Applicant Name</label>
            <PeopleAutocomplete mode="single" value={applicantName} onChange={setApplicantName} testid="sub-applicant" />
          </div>
          <div className="md:col-span-2">
            <label className={lbl}>Rating: {rating}/10</label>
            <input data-testid="sub-rating" type="range" min="0" max="10" value={rating} onChange={e=>setRating(e.target.value)} className="w-full"/>
          </div>
        </>}
        <div className="md:col-span-2">
          <label className={lbl}>Notes</label>
          <textarea data-testid="sub-notes" value={notes} onChange={e=>setNotes(e.target.value)} rows={kind === "opportunity" || kind === "wisdom" || kind === "win" ? 5 : 3} className={inp}/>
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <button type="submit" disabled={submitting} data-testid={isEdit ? `update-${kind}` : `save-${kind}`} className="al-gradient text-white px-5 py-2 rounded-full text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed">
          {submitting ? (isEdit ? "Updating…" : "Saving…") : (isEdit ? "Update entry" : "Save entry")}
        </button>
        <button type="button" disabled={submitting} onClick={onCancel} className="px-5 py-2 rounded-full text-sm text-slate-600 border border-slate-200 disabled:opacity-50">Cancel</button>
      </div>
    </form>
  );
}
